import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { SearchBox, STEPS, Modal } from "./ui";
import { TrackingTimeline } from "../../components/TrackingTimeline";
import { QRLabel } from "../../components/ScanQR";

export default function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const load = async () => { const { data } = await api.get("/admin/orders"); setOrders(data); };
  useEffect(() => { load(); }, []);
  const openDetail = async (n) => { try { const { data } = await api.get(`/admin/orders/${n}`); setDetail(data); } catch { toast.error("Could not load order"); } };
  const updateStatus = async (n, i) => { await api.put(`/admin/orders/${n}/status`, { step_index: i }); toast.success(`Order ${n} updated`); load(); if (detail?.order_number === n) openDetail(n); };
  const markPaid = async (n) => { try { await api.post(`/admin/orders/${n}/mark-paid`); toast.success(`Payment confirmed — ${n} moved to Sourcing`); load(); if (detail?.order_number === n) openDetail(n); } catch { toast.error("Could not confirm payment"); } };

  const list = useMemo(() => orders.filter((o) => {
    if (filter === "paid" && o.payment_status !== "paid") return false;
    if (filter === "pending" && o.payment_status === "paid") return false;
    if (!q) return true;
    return `${o.order_number} ${o.customer_name} ${o.customer_phone} ${o.delivery_city}`.toLowerCase().includes(q.toLowerCase());
  }), [orders, q, filter]);

  const stageLabel = (o) => o.payment_status !== "paid" ? "Pending payment" : STEPS[Math.min((o.current_step ?? 0) + 1, STEPS.length - 1)];

  return (
    <div className="space-y-4" data-testid="admin-orders">
      <div className="flex flex-wrap gap-3 items-center">
        <SearchBox value={q} onChange={setQ} placeholder="Search orders…" />
        <div className="flex gap-2">
          {["all", "paid", "pending"].map((ff) => (
            <button key={ff} onClick={() => setFilter(ff)} data-testid={`order-filter-${ff}`}
              className={`px-4 py-2 rounded-full text-sm font-medium capitalize ${filter === ff ? "bg-[#0D530E] text-white" : "bg-white border border-black/10"}`}>{ff}</button>
          ))}
        </div>
      </div>
      {list.length === 0 && <p className="text-[#5C5C5C]">No orders match.</p>}
      {list.map((o) => (
        <button key={o.order_number} onClick={() => openDetail(o.order_number)} data-testid={`order-row-${o.order_number}`}
          className="w-full text-left bg-white border border-black/5 rounded-2xl p-5 hover:border-[#306D29]/40" style={{ transition: "border-color 0.15s ease" }}>
          <div className="flex flex-wrap justify-between gap-3 items-start">
            <div>
              <p className="font-display text-lg">{o.order_number} <span className="text-xs bg-[#E7E1B1] px-2 py-0.5 rounded-full ml-1">{o.type}</span></p>
              <p className="text-sm text-[#5C5C5C]">{o.customer_name} · {o.customer_phone} · {o.delivery_city}</p>
              <p className="text-sm text-[#5C5C5C]">{o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</p>
            </div>
            <div className="text-right">
              <p className="font-display text-xl text-[#306D29]">GH₵ {o.total_ghs.toLocaleString()}</p>
              <span className={`text-xs font-semibold ${o.payment_status === "paid" ? "text-[#2D7A5D]" : "text-[#D9933B]"}`}>{o.payment_status === "paid" ? "● Paid" : "○ Pending payment"}</span>
              <p className="text-xs mt-1 text-[#0D530E] font-medium">{stageLabel(o)}</p>
            </div>
          </div>
        </button>
      ))}

      {detail && (
        <Modal title={`Order ${detail.order_number}`} onClose={() => setDetail(null)} testid="order-detail">
          <div className="space-y-5">
            <div className="flex justify-center"><QRLabel value={detail.order_number} caption="Scan at any fulfilment stage" /></div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-xs uppercase tracking-wide text-[#5C5C5C]">Customer</p><p className="font-medium">{detail.customer_name}</p><p className="text-[#5C5C5C]">{detail.customer_phone}</p></div>
              <div><p className="text-xs uppercase tracking-wide text-[#5C5C5C]">Destination</p><p className="font-medium">{detail.delivery_city}</p><p className="text-[#5C5C5C] text-xs">{detail.delivery_address}</p></div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wide text-[#5C5C5C] mb-2">Items</p>
              <div className="space-y-1.5">
                {detail.items.map((it) => (
                  <div key={it.product_id} className="flex items-center gap-2 text-sm">
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${it.purchased ? "bg-[#2D7A5D] text-white" : "border border-black/20"}`}>{it.purchased ? "✓" : ""}</span>
                    <span className="flex-1">{it.name} ×{it.quantity}</span>
                    {it.purchased && <span className="text-xs text-[#2D7A5D]">£{Number(it.actual_cost_gbp || 0).toFixed(2)}</span>}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between text-sm border-y border-black/5 py-3">
              <span className={detail.payment_status === "paid" ? "text-[#2D7A5D] font-semibold" : "text-[#D9933B] font-semibold"}>{detail.payment_status === "paid" ? "● Paid" : "○ Pending payment"}</span>
              <span className="font-display text-lg text-[#306D29]">GH₵ {detail.total_ghs?.toLocaleString()}</span>
            </div>

            {detail.payment_status !== "paid" && (
              <button onClick={() => markPaid(detail.order_number)} data-testid="confirm-payment-btn"
                className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full"
                style={{ transition: "background-color 0.2s ease" }}>
                Confirm payment received &amp; start sourcing
              </button>
            )}

            {detail.payment_status === "paid" && (
              <>
                <div>
                  <p className="text-xs uppercase tracking-wide text-[#5C5C5C] mb-3">Fulfilment timeline</p>
                  <TrackingTimeline steps={detail.steps} currentStep={detail.current_step} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-[#5C5C5C] mb-2">Advance stage</p>
                  <div className="flex flex-wrap gap-2">
                    {STEPS.map((s, i) => (
                      <button key={i} data-testid={`status-${detail.order_number}-${i}`} onClick={() => updateStatus(detail.order_number, i)}
                        className={`text-xs px-3 py-1.5 rounded-full border ${i <= detail.current_step ? "bg-[#2D7A5D] text-white border-[#2D7A5D]" : "border-black/10 hover:border-black/30"}`}>{s}</button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
